'use client';

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Portal,
  createOverlay,
} from '@chakra-ui/react';
import {
  LuBan,
  LuCheckCheck,
  LuCircleAlert,
  LuCircleCheck,
  LuInfo,
  LuOctagonX,
} from 'react-icons/lu';

import React from 'react';

interface DialogProps {
  overlayId: string;
  title?: string | React.ReactNode;
  description?: string | React.ReactNode;
  content?: React.ReactNode;
  footer?: React.ReactNode;
  handleConfirm?: () => void;
  type?: 'confirm' | 'warning' | 'error' | 'info' | 'success';
  containerProps?: Dialog.ContentProps;
  onClose?: () => void;
}

const getTypeValue = (
  type: 'confirm' | 'warning' | 'error' | 'info' | 'success',
  title?: string,
) => {
  switch (type) {
    case 'confirm':
      return { icon: LuCheckCheck, color: 'fg.secondary' };
    case 'warning':
      return { icon: LuCircleAlert, color: 'fg.warning' };
    case 'error':
      return { icon: LuOctagonX, color: 'fg.error' };
    case 'info':
      return { icon: LuInfo, color: 'fg.info' };
    case 'success':
      return { icon: LuCircleCheck, color: 'fg.success' };
  }
};

export const OverlayDialog = createOverlay<DialogProps>((props) => {
  const {
    overlayId,
    title,
    description,
    content,
    footer,
    handleConfirm = null,
    containerProps,
    onClose,
    ...rest
  } = props;

  return (
    <Dialog.Root
      placement="center"
      motionPreset="scale"
      closeOnInteractOutside={false}
      {...rest}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            gap={4}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flex={1}
            maxH="80vh"
            maxW="80vw"
            borderRadius="lg"
            boxShadow="lg"
            border="1px solid"
            borderColor="fg.subtle"
            {...containerProps}
          >
            <Dialog.Header>
              <Dialog.Title
                {...(title && {
                  textStyle: 'heading.title',
                  color: 'secondary.fg',
                })}
              >
                {title || ''}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body
              maxH="70vh"
              maxW="80vw"
              overflow="scroll"
              flex={1}
              height="100%"
              width="100%"
              px={10}
              pt={0}
              pb={10}
              gap={10}
            >
              {description && (
                <Dialog.Description
                  textStyle={'body.muted'}
                  color={'fg.subtle'}
                  mb={5}
                >
                  {description}
                </Dialog.Description>
              )}
              <Box pl={8}>{content}</Box>
            </Dialog.Body>

            {handleConfirm !== null && handleConfirm !== undefined && (
              <Dialog.Footer>
                {footer}
                <Dialog.ActionTrigger asChild>
                  <Button
                    variant="outline"
                    colorPalette={'green'}
                    color="fg.subtle"
                    size="xs"
                    data-testid="tutorial-dialog-confirm"
                    onClick={() => {
                      handleConfirm();
                      OverlayDialog.close(overlayId);
                    }}
                  >
                    <LuCircleCheck />
                    Confirm
                  </Button>
                </Dialog.ActionTrigger>
                <Dialog.ActionTrigger asChild>
                  <Button
                    variant="outline"
                    colorPalette={'red'}
                    color="fg.subtle"
                    size="xs"
                    onClick={() => OverlayDialog.close(overlayId)}
                  >
                    <LuBan />
                    Cancel
                  </Button>
                </Dialog.ActionTrigger>
              </Dialog.Footer>
            )}

            <Dialog.CloseTrigger asChild onClick={onClose}>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
